import { use, registerTheme, registerPreprocessor } from 'echarts/core'
import { BarChart, LineChart, PieChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent, LegendScrollComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
// echarts 6 moved grid.containLabel behind this feature; without it the option
// is parsed, warned about, and ignored, so long axis labels get clipped.
import { LegacyGridContainLabel } from 'echarts/features'

use([BarChart, LineChart, PieChart, GridComponent, TooltipComponent, LegendComponent, LegendScrollComponent, LegacyGridContainLabel, CanvasRenderer])

// Horizontal lines an empty plot is ruled with, counting the axis itself, so
// it reads as a chart with no data rather than a blank panel.
const emptyGridLineCount = 5

// Labels an empty category axis is thinned down to. Four spans the period
// without crowding it; an axis under twice that is already sparse enough to
// leave alone.
const emptyAxisLabelCount = 4

// A plain legend wraps onto a second row when it runs out of width, which on a
// phone pushes it up over the axis labels. The scrolling legend stays one row
// and pages instead. This has to be a preprocessor, not a theme value: echarts
// picks the legend class from the raw option before merging the theme, so
// `legend.type` set in registerTheme is read but never applied.
registerPreprocessor((option: any) => {
  const legends = [].concat(option?.legend ?? [])
  legends.forEach((legend: any) => {
    if (legend && !legend.type) legend.type = 'scroll'
  })

  // The theme holds `grid.bottom` clear for the legend. A single-series chart
  // has none, so that band would just be empty space under the axis — give it
  // back to the plot unless the chart asked for a bottom of its own.
  const hasAxes = Boolean(option?.xAxis || option?.yAxis)
  if (!legends.length && hasAxes) {
    const grids = [].concat(option.grid ?? [])
    if (!grids.length) option.grid = { bottom: 0 }
    grids.forEach((grid: any) => {
      if (grid && grid.bottom === undefined) grid.bottom = 0
    })
  }

  // Blank when there are no series, no points, or every point is zero or null.
  // The two series shapes differ — bar and line carry plain numbers, pie slices
  // carry `{ name, value }` — so each point is unwrapped before it is tested.
  // Negatives count as data; churn is plotted below the axis.
  const blank = ([].concat(option?.series ?? []) as any[]).every((entry) =>
    ([].concat(entry?.data ?? []) as any[]).every((point) => {
      const value = point && typeof point === 'object' ? point.value : point
      return value === null || value === undefined || value === 0
    }),
  )

  // With nothing to scale to, echarts falls back to 0..1 — and a chart carrying
  // `minInterval: 1` gets a line at each end and nothing between, which is what
  // makes an empty range look broken. A 0..4 axis at unit steps rules the plot
  // with five lines instead. Only the value axis is touched: the legend and the
  // buckets stay, and its numbers go, since 0 to 4 describes nothing that
  // happened.
  //
  // Both branches assign, never just one: echarts merges this option over the
  // one already on screen, so leaving a field out would keep the empty scale
  // once real data arrived.
  const valueAxes = [].concat(option?.yAxis ?? []) as any[]
  valueAxes.forEach((axis) => {
    if (!axis || axis.type !== 'value') return
    axis.max = blank ? emptyGridLineCount - 1 : null
    axis.interval = blank ? 1 : null
    axis.axisLabel = { ...axis.axisLabel, show: !blank }
  })

  // The buckets thin out for the same reason. Thirty days prints about fifteen
  // dates, which is a wall of scaffolding for a period where nothing happened;
  // four evenly spaced labels still say which period it was. Axes short enough
  // to read at a glance keep every label, and 'auto' — not null — is what puts
  // echarts back in charge once data arrives.
  const categoryAxes = [].concat(option?.xAxis ?? []) as any[]
  categoryAxes.forEach((axis) => {
    if (!axis || axis.type !== 'category') return
    const count = ([].concat(axis.data ?? []) as any[]).length
    const thin = blank && count > emptyAxisLabelCount * 2
    axis.axisLabel = {
      ...axis.axisLabel,
      interval: thin ? Math.ceil(count / emptyAxisLabelCount) - 1 : 'auto',
    }
  })
})

// Inset the grid keeps on every side so an exported image never has its axis
// labels flush against the edge. Chart mirrors it as a negative margin so
// that on screen the labels still line up with the card's own padding.
export const chartGridInset = 8

// Categorical palette + neutrals taken from the artech-ui design tokens
// (display-*-default, neutral-800, neutral-300) so charts stay on-brand.
const palette = ['#6d4fe7', '#0ecc5d', '#f76808','#0087ff', '#db41a5', '#ffce00']
const axisLabelColor = '#8d8d8d'
const splitLineColor = '#e8e8e8'

const axisLabel = { color: axisLabelColor, fontSize: 12, fontWeight: 500, lineHeight: 16, margin: 12 }

registerTheme('artech', {
  color: palette,
  backgroundColor: 'rgba(0,0,0,0)',
  textStyle: { fontFamily: 'Public Sans' },
  grid: { top: 24, left: chartGridInset, right: chartGridInset, bottom: 48, containLabel: true },
  bar: {
    itemStyle: { borderColor: '#ffffff', borderWidth: 0 },
    barMaxWidth: 28,
  },
  line: {
    lineStyle: { width: 2 },
    symbol: 'emptyCircle',
    symbolSize: 6,
    smooth: true,
  },
  // Donuts read off the shared bottom legend, so slices carry no labels and the
  // ring is centred rather than offset for a side legend.
  pie: {
    radius: ['52%', '74%'],
    center: ['50%', '46%'],
    label: { show: false },
    itemStyle: { borderColor: '#ffffff', borderWidth: 2 },
  },
  categoryAxis: {
    boundaryGap: true,
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel,
    splitLine: { show: false },
  },
  valueAxis: {
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel,
    splitLine: { show: true, lineStyle: { color: [splitLineColor] } },
  },
  legend: {
    icon: 'roundRect',
    bottom: 0,
    // Centred on the chart, inset so the items and paging arrows never reach
    // the card edge. `left`/`right` would size the box but left-align inside it.
    left: 'center',
    padding: [0, chartGridInset],
    itemWidth: 12,
    itemHeight: 12,
    itemGap: 16,
    textStyle: { color: '#202020', fontSize: 12, lineHeight: 16, padding: [0, 2] },
    // Paging controls for the scrolling legend, sized to match the items.
    pageIconSize: 10,
    pageIconColor: axisLabelColor,
    pageIconInactiveColor: splitLineColor,
    pageButtonGap: 8,
    pageTextStyle: { color: axisLabelColor, fontSize: 12, lineHeight: 16 },
  },
  tooltip: {
    axisPointer: {
      lineStyle: { color: splitLineColor, width: 1 },
      crossStyle: { color: splitLineColor, width: 1 },
    },
  },
})
